/**
 * <overlay-scroll> — contenedor con scroll interno y scrollbar overlay.
 *
 * Objetivos:
 * - El scrollbar nativo se oculta y NO reserva espacio (pixel perfect).
 * - Un thumb overlay aparece solo al hacer scroll y se auto-oculta tras un delay.
 * - Arrastrable para desplazar el contenido.
 *
 * Uso: envolver el contenido scrollable en <overlay-scroll>, renderizando desde
 * Liquid el viewport interno (scroller real) y el thumb absoluto sobre él. Es
 * importante que ambos vengan del servidor: si los creara el cliente, el morph de
 * la Section Rendering API reconciliaría una estructura distinta a la del DOM y
 * destruiría el viewport. Si faltan, se crean acá como fallback.
 * El max-height / overflow los aporta el CSS del tema (.facets-overlay-scroll).
 */

const THUMB_MIN_HEIGHT = 24;
const HIDE_DELAY_MS = 800;

class OverlayScroll extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {HTMLElement | null} */
  #thumb = null;
  /** @type {number | null} */
  #rafId = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #hideTimer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  #dragStartY = 0;
  #dragStartScroll = 0;
  #dragging = false;

  connectedCallback() {
    const { viewport, thumb } = this.#resolveParts();

    this.#viewport = viewport;
    this.#thumb = thumb;

    // Los handlers son propiedades de instancia, así que re-registrarlos sobre el
    // mismo nodo es un no-op y connectedCallback queda idempotente.
    viewport.addEventListener('scroll', this.#handleScroll, { passive: true });
    thumb.addEventListener('pointerdown', this.#handleThumbDown);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(viewport);

    this.#mutationObserver?.disconnect();
    this.#mutationObserver = new MutationObserver(this.#scheduleUpdate);
    this.#mutationObserver.observe(viewport, { childList: true, subtree: true });

    this.#update();
  }

  /**
   * Devuelve el viewport y el thumb, prefiriendo los que vienen del servidor.
   * @returns {{ viewport: HTMLElement, thumb: HTMLElement }}
   */
  #resolveParts() {
    let thumb = /** @type {HTMLElement | null} */ (this.querySelector(':scope > .overlay-scroll__thumb'));
    let viewport = /** @type {HTMLElement | null} */ (this.querySelector(':scope > .overlay-scroll__viewport'));

    if (!viewport) {
      viewport = document.createElement('div');
      viewport.className = 'overlay-scroll__viewport';
      // Mueve el contenido dentro del viewport sin arrastrar el thumb.
      for (const node of [...this.childNodes]) {
        if (node !== thumb) viewport.appendChild(node);
      }
      this.insertBefore(viewport, thumb);
    }

    if (!thumb) {
      thumb = document.createElement('div');
      thumb.className = 'overlay-scroll__thumb';
      thumb.setAttribute('aria-hidden', 'true');
      this.appendChild(thumb);
    }

    return { viewport, thumb };
  }

  disconnectedCallback() {
    if (this.#viewport) this.#viewport.removeEventListener('scroll', this.#handleScroll);
    if (this.#thumb) this.#thumb.removeEventListener('pointerdown', this.#handleThumbDown);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    if (this.#hideTimer !== null) clearTimeout(this.#hideTimer);
    window.removeEventListener('pointermove', this.#handleThumbMove);
    window.removeEventListener('pointerup', this.#handleThumbUp);
  }

  #handleScroll = () => {
    this.#reveal();
    this.#scheduleUpdate();
    this.#scheduleHide();
  };

  #scheduleUpdate = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const viewport = this.#viewport;
    const thumb = this.#thumb;
    // Si el viewport quedó fuera del DOM (por ejemplo tras un morph), sus medidas
    // son 0 y escribir sobre el thumb podría ocultar un nodo que ya no es nuestro.
    if (!viewport || !thumb || !viewport.isConnected || !thumb.isConnected) return;

    const { clientHeight, scrollHeight, scrollTop } = viewport;
    const ratio = scrollHeight > 0 ? clientHeight / scrollHeight : 1;

    if (ratio >= 1) {
      thumb.style.display = 'none';
      return;
    }
    thumb.style.display = '';

    const thumbHeight = Math.max(clientHeight * ratio, THUMB_MIN_HEIGHT);
    const maxScroll = scrollHeight - clientHeight;
    const maxThumbTop = clientHeight - thumbHeight;
    const thumbTop = maxScroll > 0 ? (scrollTop / maxScroll) * maxThumbTop : 0;

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  };

  #reveal() {
    this.classList.add('is-scrolling');
  }

  #scheduleHide = () => {
    if (this.#hideTimer !== null) clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => {
      if (!this.#dragging) this.classList.remove('is-scrolling');
    }, HIDE_DELAY_MS);
  };

  /** @param {PointerEvent} event */
  #handleThumbDown = (event) => {
    const viewport = this.#viewport;
    if (!viewport) return;
    event.preventDefault();
    this.#dragging = true;
    this.#dragStartY = event.clientY;
    this.#dragStartScroll = viewport.scrollTop;
    this.#reveal();
    window.addEventListener('pointermove', this.#handleThumbMove);
    window.addEventListener('pointerup', this.#handleThumbUp);
  };

  /** @param {PointerEvent} event */
  #handleThumbMove = (event) => {
    const viewport = this.#viewport;
    if (!viewport || !this.#dragging) return;

    const { clientHeight, scrollHeight } = viewport;
    const ratio = scrollHeight > 0 ? clientHeight / scrollHeight : 1;
    const thumbHeight = Math.max(clientHeight * ratio, THUMB_MIN_HEIGHT);
    const maxThumbTop = clientHeight - thumbHeight;
    const maxScroll = scrollHeight - clientHeight;
    if (maxThumbTop <= 0) return;

    const deltaY = event.clientY - this.#dragStartY;
    viewport.scrollTop = this.#dragStartScroll + (deltaY / maxThumbTop) * maxScroll;
  };

  #handleThumbUp = () => {
    this.#dragging = false;
    window.removeEventListener('pointermove', this.#handleThumbMove);
    window.removeEventListener('pointerup', this.#handleThumbUp);
    this.#scheduleHide();
  };
}

if (!customElements.get('overlay-scroll')) {
  customElements.define('overlay-scroll', OverlayScroll);
}
